import { Logger } from 'typescript-log'
import { GetDcrMeta } from './nielsen-meta'

let niesenV60Disabled = false

export function nielsenV60Enabled() {
    return !niesenV60Disabled
}

export function disableNielsenV60() {
    niesenV60Disabled = true
}

interface NielsenParams {
    sfcode: string
    apid: string
    apn: string
    nsdkv: string

    v60Server: string
}

interface NielsenMetadata {
    /** Required: assign this per asset */
    assetid: string
    /** Required: always use cms */
    dataSrc: string
    pageURL?: string
    referrerURL?: string
    /** Required: please discuss values with Nielsen */
    section: string
    segA?: string
    segB?: string
    segC?: string
    /** Required: the same for every static asset */
    type: string
}

interface NielsenSDK {
    ggInitialize: (params: NielsenParams) => void
    ggPM: (event: 'staticstart' | 'staticend', metadata: NielsenMetadata) => void
}

interface NOLCMB {
    getInstance: (apiId: string) => NielsenSDK
}

declare const NOLCMB: NOLCMB
declare const nielsenSdk: NielsenSDK

declare let nielsenMetadata: NielsenMetadata

let softLoadCounter = -1

declare const nielsenDcrInitialised: boolean
declare const nielsenDcrEventBuffer: (() => void)[]

declare const nielsenV60Initialised: boolean
declare const nielsenV60EventBuffer: (() => void)[]

interface NielsenTrac {
    pageEvent: (location: Location) => void
}

declare const window: Window & { trac: NielsenTrac }

function dcrSoftReload(logger: Logger, getDcrMeta: GetDcrMeta, siteName: string) {
    if (!nielsenMetadata) {
        console.error('Nielsen softPageLoad was fired before hardPageLoad!')
        return
    }

    try {
        nielsenSdk.ggPM('staticend', nielsenMetadata)

        const url = window.location.href
        const urlPath = window.location.pathname

        const { section, segA, segB } = getDcrMeta(siteName, window.location.pathname, 'web')

        nielsenMetadata = {
            ...nielsenMetadata,
            assetid: urlPath,
            pageURL: `page_${softLoadCounter}_${url}`,
            // the first soft load has its own urlPath as referrer instead of the hard load URL
            // this is intended
            referrerURL: nielsenMetadata.pageURL || url,
            section,
            segA,
            segB,
        }

        logger.debug(nielsenMetadata, 'Firing Nielsen soft page load')

        nielsenSdk.ggPM('staticstart', nielsenMetadata)
    } catch (err) {
        logger.error({ err }, 'Nielsen soft page load error')
    }
}

const v60TrackPage = () => {
    window.trac.pageEvent(location)
}

export function nielsenSoftPageLoad(logger: Logger, getDcrMeta: GetDcrMeta, siteName: string) {
    // Dont do anything when its not actually there in the case of regionals
    if (typeof nielsenDcrInitialised === 'undefined') {
        return
    }

    softLoadCounter++

    // we don't want to fire a soft page load the first time this method is called
    // as it will always be the same page as the hard load was
    if (softLoadCounter === 0) {
        return
    }

    if (nielsenDcrInitialised) {
        dcrSoftReload(logger, getDcrMeta, siteName)
    } else {
        nielsenDcrEventBuffer.push(() => dcrSoftReload(logger, getDcrMeta, siteName))
    }

    if (nielsenV60Enabled()) {
        if (nielsenV60Initialised) {
            v60TrackPage()
        } else {
            nielsenV60EventBuffer.push(() => v60TrackPage())
        }
    }
}
